import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import AccountsOverview from "@/components/accounts/AccountsOverview";
import BankRecon from "@/components/accounts/BankRecon";
import DataQuality from "@/components/accounts/DataQuality";
import MemberBills from "@/components/accounts/MemberBills";
import AccountsTeam from "@/components/accounts/AccountsTeam";
import RequestQueue from "@/components/accounts/RequestQueue";
import MonthClose from "@/components/accounts/MonthClose";
import AccountsSettings from "@/components/accounts/AccountsSettings";
import Salaries from "@/components/accounts/Salaries";
import Payables from "@/components/accounts/Payables";
import PettyCashAll from "@/components/accounts/PettyCashAll";
import TaxTab from "@/components/accounts/TaxTab";
import SaleDeletions from "@/components/accounts/SaleDeletions";
import BloodTests from "@/components/accounts/BloodTests";
import { thisMonth } from "@/components/accounts/shared";

// Accounts: the accounts team's cross-gym view (full page, no left nav; reached from the
// header dropdown). Tabs in the order the team works: bank reconciliation first.
const TABS = [
  ["recon", "Bank reconciliation"],
  ["quality", "Data quality"],
  ["bills", "Member bills"],
  ["refunds", "Refunds"],
  ["purchases", "Purchases"],
  ["blood", "Blood tests"],
  ["deletions", "Sale deletions"],
  ["salaries", "Salaries"],
  ["payables", "Bills & payables"],
  ["petty", "Petty cash"],
  ["tax", "Tax & CA pack"],
  ["close", "Month close"],
  ["overview", "Overview"],
  ["team", "Team"],
  ["settings", "Settings"],
];
const NO_MONTH = new Set(["bills", "quality", "team", "refunds", "purchases", "deletions", "close", "settings", "payables", "petty"]);
const NO_FILTERS = new Set(["bills", "team", "settings"]);   // Member bills has its own filter bar
const SELECT = "bg-[#0a0a0a] border border-[#262626] rounded-md h-9 px-2 text-sm text-zinc-200";

export default function Accounts() {
  const { facilities } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "recon";
  const [month, setMonth] = useState(params.get("month") || thisMonth());
  const [country, setCountry] = useState("");
  const [gym, setGym] = useState("");

  const gyms = useMemo(() => (facilities || []).map((f) => ({ id: f.id, name: f.name, country: f.country || "", currency_code: f.currency_code || "INR" })), [facilities]);
  const countries = useMemo(() => [...new Set(gyms.map((g) => g.country).filter(Boolean))].sort(), [gyms]);
  const gymOptions = gyms.filter((g) => !country || g.country === country);
  const setTab = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: true });

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="accounts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow">Across every gym</div>
          <h1 className="text-2xl font-semibold text-zinc-50">Accounts</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!NO_MONTH.has(tab) && (
            <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className={SELECT} data-testid="accounts-month" />
          )}
          {!NO_FILTERS.has(tab) && (
            <>
              <select value={country} onChange={(e) => { setCountry(e.target.value); setGym(""); }} className={SELECT} data-testid="accounts-country">
                <option value="">All countries</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={gym} onChange={(e) => setGym(e.target.value)} className={SELECT} data-testid="accounts-gym">
                <option value="">All gyms</option>
                {gymOptions.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0f0f0f] border border-[#262626] flex-wrap h-auto">
          {TABS.map(([k, label]) => <TabsTrigger key={k} value={k} data-testid={`accounts-tab-${k}`}>{label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="recon" className="mt-4"><BankRecon month={month} country={country} gym={gym} gyms={gyms} /></TabsContent>
        <TabsContent value="quality" className="mt-4"><DataQuality country={country} gym={gym} /></TabsContent>
        <TabsContent value="bills" className="mt-4"><MemberBills month={month} country={country} gym={gym} gyms={gyms} /></TabsContent>
        <TabsContent value="overview" className="mt-4"><AccountsOverview month={month} country={country} gym={gym} /></TabsContent>
        <TabsContent value="refunds" className="mt-4"><RequestQueue kind="refund" country={country} gym={gym} gyms={gyms} /></TabsContent>
        <TabsContent value="purchases" className="mt-4"><RequestQueue kind="purchase" country={country} gym={gym} gyms={gyms} /></TabsContent>
        <TabsContent value="blood" className="mt-4"><BloodTests month={month} country={country} gym={gym} /></TabsContent>
        <TabsContent value="deletions" className="mt-4"><SaleDeletions gym={gym} country={country} gyms={gyms} /></TabsContent>
        <TabsContent value="salaries" className="mt-4"><Salaries month={month} gym={gym} gyms={gyms} /></TabsContent>
        <TabsContent value="payables" className="mt-4"><Payables country={country} gym={gym} gyms={gyms} /></TabsContent>
        <TabsContent value="petty" className="mt-4"><PettyCashAll country={country} gym={gym} gyms={gyms} /></TabsContent>
        <TabsContent value="tax" className="mt-4"><TaxTab month={month} country={country} gym={gym} /></TabsContent>
        <TabsContent value="close" className="mt-4"><MonthClose gyms={gyms} gym={gym} /></TabsContent>
        <TabsContent value="team" className="mt-4"><AccountsTeam /></TabsContent>
        <TabsContent value="settings" className="mt-4"><AccountsSettings gyms={gyms} /></TabsContent>
      </Tabs>
    </div>
  );
}
